import { GhostButton, OutlineButton } from '../../../../widgets/buttons';
import { DropdownActionItem, DropdownActions } from '../../../../widgets/dropdown';
import { useDialogManager } from '../../../../widgets/dialog';
import { Icon } from '../../../../widgets/icon';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-relay';
import EditIcon from 'src/shared/material_icons/image/edit.svg';
import ExpandIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import CollapseIcon from 'src/shared/material_icons/navigation/expand_less.svg';
import SaveIcon from 'src/shared/material_icons/content/save.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import ProductLogicIcon from 'src/shared/material_icons/notification/account_tree.svg';
import SceneEditorIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import SettingsIcon from 'src/shared/material_icons/action/settings.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled, { css } from 'styled-components';
import { ProductStatus } from '../../../../../shared/enums';
import { backofficeUrl, T, toLocalID } from '../../../../../shared/helpers';
import { productLogicName } from '../../../../../shared/productNames';
import LogoZakeke from '../../../../../shared/svg/logoZakeke';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { puSwitchProductSetStatusMutation } from '../../../products/__generated__/puSwitchProductSetStatusMutation.graphql';
import { productStatusMutation } from '../../../products/productStatusMutation';
import { Preview3DStoreContext } from '../../../../widgets/preview3D/store';
import { editProductNameAction, setProductStatusAction, useProductConfigurationChangeStep, useProductConfigurationState } from '../../state';
import { useInlineRename } from './useInlineRename';
import { EditorDialog } from './editorDialogWindow';
import { editorPrimaryFill } from './editorPalette';

type ComposerMode = 'scene' | 'composer' | 'test';

const HeaderContainer = styled.header`
    display: grid;
    height: 52px;
    flex: 0 0 52px;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 14px;
    padding: 0 18px;
    color: var(--composer-fg, #1e2a38);
    background: var(--editor-chrome, #dbdde1);
    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        system-ui,
        sans-serif;
    z-index: 40;
`;

const HeaderLeft = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const HeaderRight = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`;

/* The full Zakeke wordmark, the way the rest of the backoffice signs its pages: cropped to a
   square it showed the Z alone, which reads as an app icon rather than as the product. It is a
   mark, not a way out — the way out is Close, at the end of the bar, which asks about unsaved work first.
   24px is the AI Studio header size, the bar this one resembles. */
const Logo = styled.span`
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;

    > svg {
        width: auto;
        height: 24px;
    }
`;

const IdentityDivider = styled.span`
    width: 1px;
    height: 20px;
    flex: 0 0 auto;
    margin: 0 4px;
    background: var(--composer-line, #dbe2e6);
`;

const Identity = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const ProductName = styled.div`
    max-width: 220px;
    overflow: hidden;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ProductNameRow = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 2px;
`;

/* Il campo prende il posto del nome con la sua misura: stesso corpo, stesso peso, nessun salto. */
const ProductNameInput = styled.input`
    width: 220px;
    min-width: 0;
    margin-left: -6px;
    padding: 2px 6px;
    color: inherit;
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-accent, #e8650a);
    border-radius: 5px;
    outline: none;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
`;

const RenameButton = styled(GhostButton).attrs({ $size: 'small', $type: 'icon' })`
    flex: 0 0 auto;
    color: var(--composer-muted, #5c6b7a);

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

// L'USCITA TORNA CLOSE, in fondo alla barra (utente, 2026-10-19): la freccia indietro a sinistra, messa
// col QA del 12/10, si toglie. Fa la stessa domanda di prima sulle modifiche non salvate.
// LA TESTATA HA UN RAGGIO E UN PESO SOLI (utente, 2026-10-21): 8px e 500, per tutti i suoi bottoni.
const headerButtonShape = css`
    border-radius: 8px;
    font-weight: 500;
`;

// CLOSE È L'USCITA, NON UN'AZIONE (utente, 2026-10-21): sta da sola dopo un filo, senza fondo, con la ×. Al passaggio un
// velo più scuro della barra: il grigio chiaro dei pannelli sulla testata non si vedrebbe.
const CloseButton = styled(GhostButton).attrs({ $size: 'medium' })`
    ${headerButtonShape}
    flex: 0 0 auto;
    gap: 6px;
    padding: 0 10px;
    white-space: nowrap;
    color: var(--editor-fg, #262a30);

    &:hover:not([disabled]) {
        background-color: rgb(0 0 0 / 7%);
        color: var(--editor-fg, #262a30);
    }

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const ProductStatusLabel = styled.span<{ $published: boolean }>`
    display: inline-flex;
    height: 24px;
    flex: 0 0 auto;
    align-items: center;
    gap: 6px;
    padding: 0 8px;
    color: var(--composer-muted, #5c6b7a);
    background: var(--composer-sunken, #f4f7fa);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px var(--composer-line, #dce3eb);
    font-size: 10.5px;

    &::before {
        width: 6px;
        height: 6px;
        background: ${(props) => (props.$published ? '#177c4c' : 'var(--composer-faint, #677078)')};
        border-radius: 50%;
        content: '';
    }
`;

// SETTINGS È UN'ICONA SENZA BORDO (prototipo, .icon-btn; utente 2026-10-21): grigia, al passaggio il fondo grigio.
// Quadrata perché non ha parola, e l'icona più grande per pesare quanto una parola.
const SettingsButton = styled(GhostButton).attrs({ $size: 'medium', $type: 'icon' })`
    ${headerButtonShape}
    flex: 0 0 auto;
    color: var(--editor-muted, #55595f);

    /* Sulla barra grigia il passaggio è un velo più scuro: il grigio chiaro dei pannelli qui non si vedrebbe. */
    &:hover:not([disabled]) {
        background-color: rgb(0 0 0 / 7%);
        color: var(--editor-fg, #262a30);
    }

    ${Icon},
    > svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

// IL CAMBIO DI EDITOR È UNA PILLOLA A DUE SEGMENTI (prototipo, .theme-seg.env-seg; utente 2026-10-21): dice dove si è,
// non è un'azione. Pista grigia incassata, il segmento corrente bianco e sollevato; gli altri grigi, scuri al passaggio.
const EditorSwitch = styled.div`
    display: inline-flex;
    flex: none;
    gap: 2px;
    padding: 2px;
    background: var(--editor-sunken, #e9ebed);
    border-radius: 999px;
    box-shadow: inset 0 0 0 1px var(--editor-line, #d1d3d8);
`;

const EditorSegment = styled.button<{ $current: boolean }>`
    display: inline-flex;
    height: 28px;
    align-items: center;
    gap: 6px;
    padding: 0 14px;
    border: 0;
    border-radius: 999px;
    background: ${(props) => (props.$current ? 'var(--editor-raised, #fff)' : 'transparent')};
    box-shadow: ${(props) => (props.$current ? '0 1px 2px rgb(30 42 56 / 5%), 0 0 0 1px rgb(30 42 56 / 6%)' : 'none')};
    color: ${(props) => (props.$current ? 'var(--editor-fg, #262a30)' : 'var(--editor-muted, #55595f)')};
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    cursor: ${(props) => (props.$current ? 'default' : 'pointer')};
    transition:
        background 140ms ease,
        color 140ms ease;

    &:hover {
        color: var(--editor-fg, #262a30);
    }

    svg {
        width: 15px;
        height: 15px;
        flex: none;
        fill: currentColor;
    }
`;

// I TRE GRUPPI DELLA DESTRA (utente, 2026-10-21): strumenti (Settings, Test mode) · salvataggio · uscita, separati da
// un filo. Prima erano bottoni uguali in fila, e cambiare editor, provare e uscire si leggevano come la stessa cosa.
const HeaderGroup = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
`;

const HeaderDivider = styled.span`
    width: 1px;
    height: 20px;
    flex: none;
    background: var(--editor-line, #c6c9ce);
`;

const SaveState = styled.span<{ $draft?: boolean; $failed?: boolean }>`
    display: inline-flex;
    height: 24px;
    align-items: center;
    gap: 6px;
    margin: 0 2px;
    padding: ${(props) => (props.$draft ? '0 9px' : '0')};
    color: var(--composer-faint, #677078);
    background: ${(props) => (props.$failed ? '#fff0f0' : props.$draft ? 'var(--composer-warn-wash, #fff0df)' : 'transparent')};
    border-radius: 999px;
    font-size: 11.5px;
    white-space: nowrap;

    ${(props) =>
        props.$draft &&
        css`
            color: ${props.$failed ? '#a52424' : 'var(--composer-warn-ink, #874b0b)'};

            &::before {
                width: 6px;
                height: 6px;
                flex: 0 0 6px;
                background: currentColor;
                border-radius: 50%;
                content: '';
            }
        `}
`;

// TEST MODE È UN BOTTONE BIANCO (prototipo: un `.btn` della topbar; utente, 2026-10-19): bordo grigio, al passaggio
// il fondo grigio. L'arancio in testata resta a Save, morbido; acceso — in test mode — prende il lavato arancio.
const HeaderButton = styled(OutlineButton).attrs({ $size: 'medium' })<{ $active?: boolean }>`
    ${headerButtonShape}
    white-space: nowrap;

    ${(props) =>
        props.$active &&
        css`
            border-color: var(--editor-accent-line, #e2b597);
            background-color: var(--editor-accent-wash, rgb(232 101 10 / 8%));
            color: var(--editor-accent-text, #b04b07);
        `}

    /* Stessa misura delle icone del selettore accanto: tre tasti in fila, un'icona sola. */
    > svg {
        width: 17px;
        height: 17px;
        flex: 0 0 17px;
        fill: currentColor;
    }
`;

const Split = styled.div`
    display: flex;
    align-items: stretch;
`;

// SAVE È IL PRIMARIO PIENO DEGLI EDITOR (utente, 2026-10-21): arancio del marchio scurito, parola bianca, al passaggio
// un passo più scuro (`editorPrimaryFill`). Il #E8650A pieno dello Storybook accanto ai bottoni tenui stonava; questo è
// l'unico pieno della testata e lo stesso dell'azione delle finestre di domanda. La freccia è separata da un filo, come
// lo split del prototipo. Il menu sotto la freccia resta il nostro: Save,
// Publish, Unpublish (utente, 2026-10-19). Il colore viene dal marchio (`secondary`), non dai token dei bottoni, che
// dentro gli editor sono quelli tenui.
const SaveButton = styled.button<{ $caret?: boolean }>`
    display: inline-flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    gap: ${(props) => (props.$caret ? '0' : '6px')};
    padding: ${(props) => (props.$caret ? '0 7px' : '0 13px')};
    color: #fff;
    background: ${(props) => editorPrimaryFill(props.theme.app.secondary).background};
    border: 0;
    border-radius: ${(props) => (props.$caret ? '0 8px 8px 0' : '8px 0 0 8px')};
    /* Il filo della freccia è chiaro e trasparente (utente, 2026-10-21): uno scuro sul fondo già scuro pesava. */
    box-shadow: ${(props) => (props.$caret ? 'inset 1px 0 0 rgb(255 255 255 / 28%)' : 'none')};
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    transition: background 0.15s;

    &:hover:not(:disabled) {
        background: ${(props) => editorPrimaryFill(props.theme.app.secondary).hover};
    }

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.app.secondary};
        outline-offset: 2px;
    }

    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
    }
`;

type HeaderToolbarProps = {
    activeMode?: ComposerMode;
    closeOverlay?: () => void;
    onSettings?: () => void;
    onSave?: () => boolean | void | Promise<boolean | void>;
    isSaving?: boolean;
    hasUnsavedChanges?: boolean;
    /** Un controllo in più al momento di uscire o salvare: modifiche che nessun campo ha segnalato (lo Scene Editor). */
    checkUnsavedChanges?: () => boolean;
    localDraftState?: 'none' | 'saving' | 'saved' | 'failed';
    /** Il prodotto ha un nome nuovo: chi tiene il «da salvare» lo sappia. */
    onProductRenamed?: () => void;
};

const PRODUCT_NAME_FIELD = 'product-name';

// USCIRE CON MODIFICHE NON SALVATE, dalla Close, cambiando editor o andando in Test mode: la stessa domanda, con un
// titolo che dice di cosa si parla. DUE STRADE E LA X (utente, 2026-10-21): salvare e procedere, o procedere senza
// salvare; dalla X si resta dove si è. Le parole dicono che cosa succede: «Save and test», «Close without saving».
type LeaveIntent = 'close' | 'switch' | 'test';

const leaveLabels = (intent: LeaveIntent) =>
    intent === 'test'
        ? { save: T._('Save and test', 'Admin'), leave: T._('Test without saving', 'Admin') }
        : intent === 'switch'
          ? { save: T._('Save and switch', 'Admin'), leave: T._('Switch without saving', 'Admin') }
          : { save: T._('Save and close', 'Admin'), leave: T._('Close without saving', 'Admin') };

const UnsavedChangesDialog: FC<{ message: string; intent: LeaveIntent; onClose: () => void; onSave: () => void; onLeave: () => void }> = ({
    message,
    intent,
    onClose,
    onSave,
    onLeave
}) => (
    <EditorDialog
        title={T._('Unsaved changes', 'Admin')}
        size='small'
        closeOnEscape
        onClose={onClose}
        buttons={[
            { label: leaveLabels(intent).leave, outline: true, onClick: onLeave },
            { label: leaveLabels(intent).save, onClick: onSave }
        ]}
    >
        <p>{message}</p>
    </EditorDialog>
);

const HeaderToolbar: FC<HeaderToolbarProps> = ({
    activeMode = 'composer',
    closeOverlay,
    onSettings,
    onSave,
    isSaving,
    hasUnsavedChanges = false,
    checkUnsavedChanges,
    localDraftState = 'none',
    onProductRenamed
}) => {
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const { showDialog, closeDialog } = useDialogManager();

    // IL NOME DEL PRODOTTO SI CAMBIA DOVE SI LEGGE (QA, 2026-10-12): un prodotto nato da zero salta il
    // passo del wizard che lo rinomina, e qui non c'era modo di dargli un nome suo.
    //
    // E STA IN DUE POSTI: lo stato della configurazione, che salva lo Scene Editor, e il modello 3D, che
    // salvano Product Logic e il VTO. Scritto in uno solo, il prossimo salvataggio dell'altro lo
    // rimetterebbe com'era. Il modello si legge al momento del gesto, dallo store che lo tiene.
    //
    // SOLO PER I PRODOTTI NATI DA ZERO (Elena, 2026-10-12): sono quelli del canale senza store
    // (`NONE`). Un prodotto di uno store prende il nome dallo store, e qui non si tocca.
    const preview3DStore = useContext(Preview3DStoreContext);
    const canRename = state.ecommerceSalesChannel.includes('NONE');
    const rename = useInlineRename();
    const startRename = () => rename.start(PRODUCT_NAME_FIELD, state.product.name);
    const renameProduct = (name: string) => {
        dispatch(editProductNameAction(name));
        preview3DStore?.getState().zkProduct?.set('name', name);
        onProductRenamed?.();
    };
    const [setStatusProduct, isUpdatingStatus] = useMutation<puSwitchProductSetStatusMutation>(productStatusMutation);
    const changeStep = useProductConfigurationChangeStep();
    const navigate = useNavigate();
    const location = useLocation();
    const isDirty = hasUnsavedChanges || unsavedChanges.isDirty;
    // Al momento di agire si guarda anche ciò che i campi non hanno detto (l'impronta della scena).
    const isDirtyNow = () => isDirty || Boolean(checkUnsavedChanges?.());
    const isPublished = state.product.statusID === ProductStatus.Published;
    const isBusy = Boolean(isSaving || isUpdatingStatus);
    const unsavedExitMessage =
        localDraftState === 'failed'
            ? T._('Your changes are not saved, and this browser could not update the local draft.', 'Admin')
            : localDraftState !== 'none'
              ? T._('Your changes are kept as a local draft, but they are not saved to the product yet.', 'Admin')
              : T._('You will lose all unsaved changes.', 'Admin');
    const editorModes = [
        {
            key: 'scene' as const,
            icon: <SceneEditorIcon />,
            name: T._('3D Scene Editor', 'Admin'),
            description: T._('Objects, materials, lights and cameras', 'Admin')
        },
        {
            key: 'composer' as const,
            icon: <ProductLogicIcon />,
            name: productLogicName(),
            description: T._('Groups, attributes, and what an option does', 'Admin')
        }
    ];
    // In test mode neither editor is current: the switcher keeps naming where Test mode came from, and Test mode itself lights up.

    const enterMode = (mode: ComposerMode) => {
        closeOverlay?.();
        if (mode === 'scene') changeStep('3D-product-configurator-scene-editor');
        if (mode === 'composer') changeStep('3D-product-configurator-composer');
        if (mode === 'test') {
            const productID = toLocalID(state.product.id);
            if (productID != null) {
                const params = new URLSearchParams({ returnTo: `${location.pathname}${location.search}` });
                navigate(`${backofficeUrl(`/test-mode/${productID}`)}?${params.toString()}`);
            }
        }
    };

    // CAMBIARE EDITOR È USCIRE DA QUESTO, come premere Close: quel che non è salvato resta indietro
    // uguale. La domanda quindi è la stessa, e si fa prima di andarsene — non dopo.
    const goToMode = (mode: ComposerMode) => {
        if (mode === activeMode) return;
        if (!isDirtyNow()) {
            enterMode(mode);
            return;
        }
        showDialog(
            'switch-mode-dialog',
            <UnsavedChangesDialog
                message={unsavedExitMessage}
                intent={mode === 'test' ? 'test' : 'switch'}
                onClose={() => closeDialog('switch-mode-dialog')}
                onSave={async () => {
                    closeDialog('switch-mode-dialog');
                    // Si procede solo se il salvataggio è andato: se no si resta, con l'errore che Save ha già mostrato.
                    if (await saveChanges(false)) enterMode(mode);
                }}
                onLeave={() => {
                    closeDialog('switch-mode-dialog');
                    enterMode(mode);
                }}
            />
        );
    };

    // IL PANNELLO SI CHIUDE SOLO USCENDO DAVVERO: chiuso prima della domanda, chi poi resta trovava
    // l'Inspector vuoto.
    const requestClose = () => {
        if (!isDirtyNow()) {
            closeOverlay?.();
            navigate(backofficeUrl('/products'));
            return;
        }
        showDialog(
            'close-dialog',
            <UnsavedChangesDialog
                message={unsavedExitMessage}
                intent='close'
                onClose={() => closeDialog('close-dialog')}
                onSave={async () => {
                    closeDialog('close-dialog');
                    if (!(await saveChanges(false))) return;
                    closeOverlay?.();
                    navigate(backofficeUrl('/products'));
                }}
                onLeave={() => {
                    closeDialog('close-dialog');
                    closeOverlay?.();
                    navigate(backofficeUrl('/products'));
                }}
            />
        );
    };

    const saveChanges = async (notifyWhenClean = true) => {
        if (!isDirtyNow()) {
            if (notifyWhenClean) showInfoNotification({ message: T._('Nothing to save — everything is up to date.', 'Admin') });
            return true;
        }
        if (!onSave) {
            showErrorNotification({ message: T._('Saving is not available in this view.', 'Admin') });
            return false;
        }

        try {
            const saved = await onSave();
            return saved !== false;
        } catch {
            return false;
        }
    };

    const changePublicationStatus = async (nextStatus: ProductStatus) => {
        if (!state.product.id || isBusy) return;
        if (isDirtyNow() && !(await saveChanges(false))) return;

        const publishing = nextStatus === ProductStatus.Published;
        if (publishing && state.ecommerceSalesChannel[0] === 'NONE') {
            navigate(`${backofficeUrl(`/test-mode/${toLocalID(state.product.id)}`)}?publish=true`);
            return;
        }
        const changed = await new Promise<boolean>((resolve) => {
            setStatusProduct({
                variables: {
                    input: {
                        productID: state.product.id!,
                        status: publishing ? 'PUBLISHED' : 'UN_PUBLISHED'
                    }
                },
                onCompleted: (response, errors) => {
                    const messages = [
                        ...(errors?.map((error) => error.message) ?? []),
                        ...(response.setStatusProduct?.errors?.map((error) => error.message).filter(Boolean) ?? [])
                    ];
                    if (!response.setStatusProduct?.model || messages.length > 0) {
                        showErrorNotification({
                            message:
                                messages.join(' ') ||
                                (publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin'))
                        });
                        resolve(false);
                        return;
                    }

                    dispatch(setProductStatusAction(nextStatus));
                    resolve(true);
                },
                onError: () => {
                    showErrorNotification({
                        message: publishing ? T._('Failed to publish the product.', 'Admin') : T._('Failed to unpublish the product.', 'Admin')
                    });
                    resolve(false);
                }
            });
        });

        if (changed) {
            showSuccessNotification({
                message: publishing ? T._('Product published.', 'Admin') : T._('Product unpublished.', 'Admin')
            });
        }
    };

    return (
        <HeaderContainer>
            <HeaderLeft>
                <Logo role='img' aria-label='Zakeke'>
                    <LogoZakeke />
                </Logo>
                <IdentityDivider aria-hidden='true' />
                <Identity>
                    {canRename && rename.editingId === PRODUCT_NAME_FIELD ? (
                        <ProductNameInput aria-label={T._('Product name', 'Admin')} {...rename.fieldProps(state.product.name, renameProduct)} />
                    ) : (
                        <ProductNameRow>
                            <ZTooltip title={state.product.name} position='bottom' isOverflowEnabled>
                                <ProductName onDoubleClick={canRename ? startRename : undefined}>
                                    {state.product.name || T._('Product configurator', 'Admin')}
                                </ProductName>
                            </ZTooltip>
                            {canRename && (
                                <ZTooltip title={T._('Rename product', 'Admin')} position='bottom'>
                                    <RenameButton type='button' aria-label={T._('Rename product', 'Admin')} onClick={startRename}>
                                        <Icon>
                                            <EditIcon />
                                        </Icon>
                                    </RenameButton>
                                </ZTooltip>
                            )}
                        </ProductNameRow>
                    )}
                    <ProductStatusLabel $published={isPublished}>{isPublished ? T._('Published', 'Admin') : T._('Not published', 'Admin')}</ProductStatusLabel>
                </Identity>
            </HeaderLeft>
            <EditorSwitch role='group' aria-label={T._('Configurator mode', 'Admin')}>
                {editorModes.map((mode) => (
                    <ZTooltip key={mode.key} title={mode.description} position='bottom'>
                        <EditorSegment
                            type='button'
                            $current={activeMode === mode.key}
                            aria-pressed={activeMode === mode.key}
                            onClick={() => activeMode !== mode.key && goToMode(mode.key)}
                        >
                            {mode.icon}
                            {mode.name}
                        </EditorSegment>
                    </ZTooltip>
                ))}
            </EditorSwitch>
            <HeaderRight>
                <HeaderGroup>
                    <ZTooltip title={T._('Settings', 'Admin')} position='bottom'>
                        <SettingsButton aria-label={T._('Settings', 'Admin')} onClick={onSettings}>
                            <Icon>
                                <SettingsIcon />
                            </Icon>
                        </SettingsButton>
                    </ZTooltip>
                    {/* TEST MODE È UNO SOLO PER TUTTA LA PIATTAFORMA (utente, 2026-09-22): è la prova del
                    cliente, e da qualunque ambiente si parta è la stessa prova. Per un po' nello Scene
                    Editor questo tasto ha fatto un'altra cosa — accendere il fondale — e due tasti con lo
                    stesso nome che fanno cose diverse sono il modo più rapido per non fidarsi più di
                    nessuno dei due. Il fondale adesso è un interruttore sul palco, dove stanno gli altri
                    modi di guardare. */}
                    <HeaderButton $active={activeMode === 'test'} onClick={() => goToMode('test')}>
                        <PlayIcon />
                        {T._('Test mode', 'Admin')}
                    </HeaderButton>
                </HeaderGroup>
                <HeaderDivider aria-hidden='true' />
                <HeaderGroup>
                    <ZTooltip
                        title={
                            isDirty && localDraftState !== 'none'
                                ? T._('This browser has a temporary copy. Use Save to persist it to the product history.', 'Admin')
                                : undefined
                        }
                        position='bottom'
                    >
                        <SaveState $draft={isDirty && localDraftState !== 'none'} $failed={localDraftState === 'failed'} aria-live='polite'>
                            {isSaving
                                ? T._('Saving…', 'Admin')
                                : isUpdatingStatus
                                  ? T._('Updating publication…', 'Admin')
                                  : isDirty && localDraftState === 'saving'
                                    ? T._('Saving local draft…', 'Admin')
                                    : isDirty && localDraftState === 'saved'
                                      ? T._('Local draft · Not saved', 'Admin')
                                      : isDirty && localDraftState === 'failed'
                                        ? T._('Draft · Local save failed', 'Admin')
                                        : isDirty
                                          ? T._('Unsaved changes', 'Admin')
                                          : T._('Saved', 'Admin')}
                        </SaveState>
                    </ZTooltip>
                    <Split>
                        <SaveButton disabled={isBusy} onClick={() => void saveChanges()}>
                            <SaveIcon />
                            {isSaving ? T._('Saving…', 'Admin') : T._('Save', 'Admin')}
                        </SaveButton>
                        <DropdownActions
                            header={({ isOpen }) => (
                                <SaveButton $caret disabled={isBusy} aria-expanded={isOpen} aria-label={T._('More save options', 'Admin')}>
                                    {isOpen ? <CollapseIcon /> : <ExpandIcon />}
                                </SaveButton>
                            )}
                            items={[
                                <DropdownActionItem key='save' caption={T._('Keep your changes', 'Admin')} disabled={isBusy} onClick={() => void saveChanges()}>
                                    {T._('Save', 'Admin')}
                                </DropdownActionItem>,
                                <DropdownActionItem
                                    key='publish'
                                    caption={T._('Put this product in the storefront', 'Admin')}
                                    disabled={isBusy || isPublished || !state.product.id}
                                    onClick={() => void changePublicationStatus(ProductStatus.Published)}
                                >
                                    {T._('Publish', 'Admin')}
                                </DropdownActionItem>,
                                <DropdownActionItem
                                    key='unpublish'
                                    caption={T._('Take it out of the storefront', 'Admin')}
                                    disabled={isBusy || !isPublished || !state.product.id}
                                    onClick={() => void changePublicationStatus(ProductStatus.UnPublished)}
                                >
                                    {T._('Unpublish', 'Admin')}
                                </DropdownActionItem>
                            ]}
                            minWidth='220px'
                            orientation='RIGHT'
                        />
                    </Split>
                </HeaderGroup>
                <HeaderDivider aria-hidden='true' />
                <CloseButton type='button' onClick={requestClose}>
                    <CloseIcon />
                    {T._('Close', 'Admin')}
                </CloseButton>
            </HeaderRight>
        </HeaderContainer>
    );
};

export default HeaderToolbar;
